import { useEffect, useState, useCallback } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Badge, Card, ErrorState } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Fields";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

interface CustomerCareConfig {
  enabled: boolean;
  publicUserId: string | null;
  valid: boolean;
  problem: string | null;
  active: boolean;
  account: { publicUserId: string; username: string | null; avatarUrl: string | null } | null;
  ratePerMinute: number;
  dailyCap: number;
  maxChars: number;
  cooldownMode: "none" | "custom";
  cooldownSeconds: number;
  unreadWaiting: number;
}

/**
 * Round 56 - Customer Care account. Separate from the System Sender: that account is one-way notifications, this one is the
 * account users write to (icon top-right of the Chat screen). The id is validated by the database on save, and again on every
 * message, so an account that is later deleted or banned switches the feature off by itself.
 */
export function CustomerCarePage() {
  const { showToast } = useToast();
  const [cfg, setCfg] = useState<CustomerCareConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [publicId, setPublicId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [rate, setRate] = useState("20");
  const [daily, setDaily] = useState("200");
  const [chars, setChars] = useState("2000");
  const [cdMode, setCdMode] = useState<"none" | "custom">("none");
  const [cdSecs, setCdSecs] = useState("30");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const apply = (c: CustomerCareConfig) => {
    setCfg(c);
    setPublicId(c.publicUserId ?? "");
    setEnabled(c.enabled);
    setRate(String(c.ratePerMinute));
    setDaily(String(c.dailyCap));
    setChars(String(c.maxChars));
    setCdMode(c.cooldownMode === "custom" ? "custom" : "none");
    setCdSecs(String(c.cooldownSeconds ?? 30));
  };
  const load = useCallback(() => {
    setError(null);
    apiRequest<CustomerCareConfig>("/admin/customer-care").then(apply).catch((e) => setError(e instanceof ApiError ? e.message : "could not load"));
  }, []);
  useEffect(load, [load]);

  async function save() {
    setFormError(null);
    setSaving(true);
    try {
      const out = await apiRequest<CustomerCareConfig>("/admin/customer-care", {
        method: "POST",
        body: { publicUserId: publicId.trim(), enabled, ratePerMinute: Number(rate), dailyCap: Number(daily), maxChars: Number(chars), cooldownMode: cdMode, cooldownSeconds: Number(cdSecs) },
      });
      apply(out);
      showToast("Customer Care saved", "success");
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell title="Customer Care">
      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : !cfg ? (
        <p className="text-sm text-ink-muted">Loading…</p>
      ) : (
        <div className="grid max-w-4xl gap-5 lg:grid-cols-2">
          <Card className="space-y-4 p-5">
            <div>
              <h2 className="text-sm font-semibold text-ink">Account</h2>
              <p className="mt-1 text-xs text-ink-faint">
                Enter the account&apos;s public User ID (the number shown on its profile). Users get a Customer Care icon at the top right of the Chat
                screen that opens a conversation with it; anyone can start one. This is separate from the System Sender account.
              </p>
            </div>
            <Input label="Public User ID" id="cc-id" inputMode="numeric" value={publicId} onChange={(e) => setPublicId(e.target.value)} />
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Customer Care enabled (icon visible to users)
            </label>
            <div className="grid grid-cols-3 gap-3">
              <Input label="Messages / minute" id="cc-rate" type="number" min={1} max={120} value={rate} onChange={(e) => setRate(e.target.value)} />
              <Input label="Messages / day" id="cc-day" type="number" min={1} max={5000} value={daily} onChange={(e) => setDaily(e.target.value)} />
              <Input label="Max characters" id="cc-chars" type="number" min={50} max={5000} value={chars} onChange={(e) => setChars(e.target.value)} />
            </div>
            <div className="space-y-2 rounded-lg border border-line p-3">
              <p className="text-sm font-semibold text-ink">Reply cooldown</p>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="radio" name="cc-cd" checked={cdMode === "none"} onChange={() => setCdMode("none")} />
                No cooldown (unlimited replies)
              </label>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="radio" name="cc-cd" checked={cdMode === "custom"} onChange={() => setCdMode("custom")} />
                Custom cooldown
              </label>
              <Input label="Seconds between messages (1-3600; use less than 60 for a short wait)" id="cc-cd" type="number" min={1} max={3600}
                value={cdSecs} disabled={cdMode !== "custom"} onChange={(e) => setCdSecs(e.target.value)} />
              <p className="text-xs text-ink-faint">
                Users see a countdown and cannot send until it ends. It is enforced by the database with the server clock, so it is the same on every device,
                survives restarts and cannot be bypassed by calling the API directly. The per-minute / per-day limits above and the duplicate-message guard stay on in both modes.
                This setting only affects Customer Care; the System Sender&apos;s &quot;allow replies&quot; setting is separate.
              </p>
            </div>
            <p className="text-xs text-ink-faint">
              Users can write to this account without the ordinary chat limits (level requirement, chat bans, block-by-recipient), but these anti-spam limits
              still apply per user, and repeated identical messages are rejected.
            </p>
            {formError && <p className="text-xs text-danger">{formError}</p>}
            <Button onClick={save} isLoading={saving}>Save</Button>
          </Card>

          <Card className="space-y-3 p-5">
            <h2 className="text-sm font-semibold text-ink">Current status</h2>
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={cfg.active ? "success" : "neutral"}>{cfg.active ? "Live in the app" : "Hidden in the app"}</Badge>
              {cfg.enabled && !cfg.valid && <Badge tone="danger">account problem</Badge>}
            </div>
            {cfg.problem && <p className="text-xs text-danger">{cfg.problem}</p>}
            {cfg.account ? (
              <div className="flex items-center gap-3">
                {cfg.account.avatarUrl ? <img src={cfg.account.avatarUrl} alt="" className="h-12 w-12 rounded-full object-cover" /> : <div className="h-12 w-12 rounded-full bg-surface-raised" />}
                <div>
                  <p className="text-sm font-medium text-ink">{cfg.account.username ?? "(no name)"}</p>
                  <p className="text-xs text-ink-faint">ID {cfg.account.publicUserId}</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-ink-faint">No account configured.</p>
            )}
            <p className="text-sm text-ink-muted">Unread user messages waiting: <span className="font-semibold text-ink">{cfg.unreadWaiting}</span></p>
            <p className="text-xs text-ink-faint">
              Changes apply immediately. If the account is later deleted or banned, the icon disappears and sending stops until you pick a valid account.
            </p>
          </Card>
        </div>
      )}
    </AppShell>
  );
}
